import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAdminApi from "@/components/admin/useAdminApi";
import StatsCards from "@/components/admin/StatsCards";
import LeadsTable from "@/components/admin/LeadsTable";
import SettingsPanel from "@/components/admin/SettingsPanel";
import BioPanel from "@/components/admin/BioPanel";
import EmailsPanel from "@/components/admin/EmailsPanel";

function Dashboard() {
  const call = useAdminApi();
  const [role, setRole] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);

  const started = useRef(false);

  const load = useCallback(async (q = "") => {
    try {
      const [s, l] = await Promise.all([call("get", "/admin/stats"), call("get", `/admin/leads?q=${encodeURIComponent(q)}`)]);
      setStats(s);
      setLeads(l);
    } catch {
      toast.error("Impossibile caricare i dati");
    }
  }, [call]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    call("get", "/me").then((m) => {
      setRole(m.role);
      if (m.role === "admin") load();
    }).catch(() => setRole("error"));
  }, [call, load]);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa iscritta?")) return;
    try {
      await call("delete", `/admin/leads/${id}`);
      toast.success("Eliminata");
      load();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  if (role === null) return <p className="p-10 text-[#5A625C]">Caricamento…</p>;
  if (role !== "admin")
    return (
      <div className="p-10 max-w-md" data-testid="admin-forbidden">
        <p className="font-display text-3xl">Area riservata</p>
        <p className="text-[#5A625C] mt-2">Questa dashboard è solo per Chiara. Accedi con l'email autorizzata.</p>
      </div>
    );

  return (
    <Tabs defaultValue="leads" className="w-full">
      <TabsList className="mb-5">
        <TabsTrigger value="leads" data-testid="admin-tab-leads">Iscritte</TabsTrigger>
        <TabsTrigger value="emails" data-testid="admin-tab-emails">Email</TabsTrigger>
        <TabsTrigger value="bio" data-testid="admin-tab-bio">Bio Instagram</TabsTrigger>
        <TabsTrigger value="settings" data-testid="admin-tab-settings">Impostazioni</TabsTrigger>
      </TabsList>
      <TabsContent value="leads" className="space-y-4">
        <StatsCards stats={stats} />
        <LeadsTable leads={leads} onSearch={load} onDelete={remove} call={call} />
      </TabsContent>
      <TabsContent value="emails"><EmailsPanel call={call} /></TabsContent>
      <TabsContent value="bio"><BioPanel /></TabsContent>
      <TabsContent value="settings" forceMount className="data-[state=inactive]:hidden"><SettingsPanel call={call} /></TabsContent>
    </Tabs>
  );
}

export default function Admin() {
  const { isLoaded } = useAuth();
  if (!isLoaded) return null;
  return (
    <>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
      <Show when="signed-in">
        <div className="min-h-screen bg-[#F9F7F3] text-[#1E231F]" data-testid="admin-page">
          <header className="border-b border-[#E5E1D8] bg-white/70 backdrop-blur-xl">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
              <Link to="/" className="font-display text-xl" data-testid="admin-home-link">Chiara Piccioni · <span className="text-[#5A625C]">Dashboard</span></Link>
              <UserButton />
            </div>
          </header>
          <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8"><Dashboard /></main>
        </div>
      </Show>
    </>
  );
}
